import { Trans, useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { useDeleteClientsBulk } from '../../hooks';
import { Client } from '../../types';

const MAX_VISIBLE_CLIENTS = 5;

interface DeleteClientsModalProps {
  clientsForDelete: Client[];
  onSuccess: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * A comprehensive client deletion modal supporting both single and bulk client deletion.
 * Provides clear warnings about permanent data loss with detailed consequence messaging.
 *
 * **Key Features:**
 * - **Dual Mode Operation**: Handles both single and multiple client deletions
 * - **Smart Messaging**: Dynamic content based on single vs. multiple clients
 * - **Comprehensive Warnings**: Detailed list of what data will be permanently lost
 * - **Bulk Operation Support**: Efficiently processes multiple client deletions
 * - **API Integration**: Handles async deletion operations with proper error handling
 * - **Data Consistency**: Invalidates relevant query caches after successful deletion
 * - **Flexible Triggers**: Supports custom triggers or falls back to default destructive button
 * - **Loading States**: Shows proper loading feedback during dangerous operations
 *
 * **Deletion Process:**
 * 1. User selects clients for deletion (single or multiple)
 * 2. Modal shows clear warnings about permanent data loss
 * 3. User confirms understanding of consequences
 * 4. API call permanently deletes clients and all associated data
 * 5. Success invalidates client caches and closes modal
 * 6. Error keeps modal open for retry
 *
 * **Data Permanently Lost:**
 * - Client information and metadata
 * - Associated projects
 * - Invoices and billing data
 * - Client-related documents and notes
 *
 * **Safety Considerations:**
 * - Uses destructive button styling to indicate danger
 * - Requires explicit confirmation
 * - Cannot be undone once confirmed
 * - Recommend archiving instead for data preservation
 *
 * @example
 * ```tsx
 * // Single client deletion
 * <DeleteClientsModal
 *   clientsForDelete={[selectedClient]}
 *   onSuccess={() => {
 *     toast.success('Client deleted successfully');
 *     navigate('/clients');
 *   }}
 * />
 *
 * // Multiple clients deletion
 * const selectedClients = [client1, client2, client3];
 * <DeleteClientsModal
 *   clientsForDelete={selectedClients}
 *   onSuccess={() => {
 *     toast.success(`${selectedClients.length} clients deleted`);
 *     clearSelection();
 *     queryClient.invalidateQueries(['clients']);
 *   }}
 * />
 *
 * // With custom trigger button
 * <DeleteClientsModal
 *   clientsForDelete={[client]}
 *   trigger={
 *     <Button variant="destructive" size="sm">
 *       <Trash2 className="w-4 h-4 mr-2" />
 *       Delete Forever
 *     </Button>
 *   }
 *   onSuccess={() => refetchClients()}
 * />
 *
 * // Bulk delete with selection management
 * function ClientBulkActions() {
 *   const [selectedClients, setSelectedClients] = useState<Client[]>([]);
 *   const [showDeleteModal, setShowDeleteModal] = useState(false);
 *
 *   const handleBulkDelete = () => {
 *     if (selectedClients.length === 0) return;
 *     setShowDeleteModal(true);
 *   };
 *
 *   return (
 *     <>
 *       <Button
 *         variant="destructive"
 *         onClick={handleBulkDelete}
 *         disabled={selectedClients.length === 0}
 *       >
 *         Delete Selected ({selectedClients.length})
 *       </Button>
 *
 *       <DeleteClientsModal
 *         clientsForDelete={selectedClients}
 *         open={showDeleteModal}
 *         onOpen={() => setShowDeleteModal(true)}
 *         onClose={() => setShowDeleteModal(false)}
 *         onSuccess={() => {
 *           setSelectedClients([]);
 *           setShowDeleteModal(false);
 *           toast.success('Clients deleted successfully');
 *         }}
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export function DeleteClientsModal({
  clientsForDelete,
  trigger,
  open,
  onOpen,
  onClose,
  onSuccess,
}: DeleteClientsModalProps) {
  const { t } = useTranslation(['authenticated.clients', 'common']);

  const clientNames = clientsForDelete.map(c => c.name);
  const visibleClientNames = clientNames.slice(0, MAX_VISIBLE_CLIENTS);
  const remainingClientNames = clientNames.slice(MAX_VISIBLE_CLIENTS);
  const remainingCount = remainingClientNames.length;

  const clientIds = clientsForDelete.map(c => c.id);

  const { mutate, isPending } = useDeleteClientsBulk({
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
  });

  const handleDelete = () => {
    mutate(clientIds);
  };

  const description = (
    <div>
      <Trans
        i18nKey="authenticated.clients:delete.deleteClientsMessage"
        count={clientsForDelete.length}
        values={{
          clients: visibleClientNames.join(', '),
          client: visibleClientNames.join(', '),
        }}
        components={{ b: <b /> }}
      />
      {remainingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help font-bold">
              {t('authenticated.clients:delete.andMoreClients', { count: remainingCount })}
            </span>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-md">
              <p>{remainingClientNames.join(', ')}</p>
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      )}
    </div>
  );

  const additionalContent = (
    <div className="text-sm text-muted-foreground">
      <Trans
        i18nKey="authenticated.clients:delete.attention"
        t={t}
        components={{
          b: <b />,
        }}
      />
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.clients:delete.deleteClients', {
        count: clientsForDelete.length,
      })}
      message={description}
      confirmText={
        isPending
          ? t('authenticated.clients:delete.deleting')
          : t('authenticated.clients:delete.deleteClientsButton', {
              count: clientsForDelete.length,
            })
      }
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={isPending}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
    >
      {additionalContent}
    </ConfirmationModal>
  );
}
